import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const DatenschutzContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 2rem;
  
  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }
  
  p {
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
    
    strong {
      color: ${props => props.theme.colors.text};
      font-weight: 600;
    }
    
    a {
      color: ${props => props.theme.colors.primary};
      text-decoration: none;
      transition: color 0.3s ease;
      
      &:hover {
        color: ${props => props.theme.colors.secondary};
        text-decoration: underline;
      }
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 1rem 0;
    
    li {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;
      color: ${props => props.theme.colors.textLight};
      line-height: 1.6;
      
      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: ${props => props.theme.colors.primary};
        font-weight: bold;
      }
    }
  }
`;

const Datenschutz = () => {
  return (
    <DatenschutzContainer>
      <Helmet>
        <title>Datenschutzerklärung - Der Ponystall | Marcella Deeken</title>
        <meta name="description" content="Datenschutzerklärung von Der Ponystall - Marcella Deeken. Informationen zum Umgang mit Ihren persönlichen Daten." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Datenschutzerklärung
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2>Datenschutz</h2>
            <p>
              Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre
              personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften
              sowie dieser Datenschutzerklärung.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2>Verantwortliche Stelle</h2>
             <p>
               <strong>Der Ponystall – Marcella Deeken</strong><br />
               Weidenweg 2, 26683 Saterland<br />
               E-Mail: <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a><br />
               Telefon: <a href="tel:01621353794">0162 1353794</a>
             </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h2>Erhobene Daten und Zwecke</h2>
            <ul>
              <li>Kontaktdaten zur Terminvereinbarung und Kommunikation</li>
              <li>Buchungsdaten zur Abwicklung von Angeboten</li>
              <li>Zahlungsdaten über unseren Dienstleister Stripe</li>
              <li>Authentifizierungsdaten (Admin-Bereich) über Clerk</li>
            </ul>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h2>Rechtsgrundlagen</h2>
            <p>
              Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung), Art. 6 Abs. 1 lit. c DSGVO (rechtliche
              Verpflichtung), Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse) sowie ggf. Art. 6 Abs. 1
              lit. a DSGVO (Einwilligung) für Marketing-Kommunikation.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <h2>Stripe</h2>
            <p>
              Für die Zahlungsabwicklung nutzen wir Stripe. Anbieter ist die Stripe Payments Europe, Ltd.,
              1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irland. Details zur Datenverarbeitung
              finden Sie in der Datenschutzerklärung von Stripe.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <h2>Clerk</h2>
            <p>
              Für die Authentifizierung im Admin-Bereich verwenden wir Clerk. Anbieter ist die Clerk Inc.
              Es gelten die Datenschutzbestimmungen von Clerk.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
          >
            <h2>Speicherdauer</h2>
            <p>
              Wir speichern personenbezogene Daten nur so lange, wie es für die jeweiligen Zwecke
              erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
          >
            <h2>Ihre Rechte</h2>
            <ul>
              <li>Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung</li>
              <li>Widerspruch gegen die Verarbeitung</li>
              <li>Datenübertragbarkeit</li>
              <li>Beschwerderecht bei einer Aufsichtsbehörde</li>
            </ul>
          </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 1.8 }}
           >
             <h2>Hosting</h2>
             <p>
               <strong>IONOS SE</strong><br />
               Elgendorfer Str. 57<br />
               56410 Montabaur<br />
               Website: <a href="https://www.ionos.de" target="_blank" rel="noopener noreferrer">www.ionos.de</a>
             </p>
           </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 2.0 }}
           >
             <h2>Technische Umsetzung & Design</h2>
             <p>
               <strong>DeekenDigital by Konstantin Deeken</strong><br />
               Weidenweg 2, 26683 Saterland<br />
               E-Mail: <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a><br />
               Website: <a href="https://deeken.digital" target="_blank" rel="noopener noreferrer">Deeken.Digital</a>
             </p>
           </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 2.2 }}
           >
             <h2>Kontakt</h2>
             <p>
               Bei Fragen wenden Sie sich bitte an die oben genannte verantwortliche Stelle.
             </p>
           </ContentCard>
        </Container>
      </ContentSection>
    </DatenschutzContainer>
  );
};

export default Datenschutz;


